import { Box, Button, Checkbox, Flex, Link, VStack } from "@chakra-ui/react";
import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	PaginationState,
	Row,
	SortingState,
	Updater,
	useReactTable,
} from "@tanstack/react-table";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useContext, useReducer, useState } from "react";
import DebouncedInput from "../inputs/DebouncedInput";
import { PaginatedFilter, PaginatedQuery } from "../../types/axios.types";
import {
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DataTableContext } from "../../hooks/data-table.hooks";

export type ActionComponentProps = {
	filters?: PaginatedFilter;
	setFilters?: (key: string, value: string) => void;
};

export type Cell = {
	id: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	row: any;
};

const RowContext = ({
	children,
	onDragEnd,
}: {
	children: React.ReactNode;
	onDragEnd?: (event: DragEndEvent) => void;
}) => {
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	if (!onDragEnd) return children;
	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={onDragEnd}
		>
			<SortableContext items={[]} strategy={verticalListSortingStrategy}>
				{children}
			</SortableContext>
		</DndContext>
	);
};

type DataTableProps = {
	/// CORE
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	columns: ColumnDef<any, any>[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	paginatedQuery: PaginatedQuery<any>;
	/// STYLE
	w?: string[];
	flexGrow?: number[];
	defaultSortBy?: SortingState;
	defaultFilters?: PaginatedFilter;
	noDataComponent?: () => React.ReactNode;
	hasRowsSelection?: boolean;
	rowSelection?: { [key: string]: boolean };
	setRowSelection?: (selection: { [key: string]: boolean }) => void;
	// Actions handlers
	cellLink?: (cell: Cell) => string | undefined;
	handleCellClick?: (cell: Cell) => void;
	rowSelectionLimit?: number;
	canSelectAllRows?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	additionalParams?: any;
	onDragEnd?: (event: DragEndEvent) => void;
	toolbar?: React.ReactNode;
};

const useServerSideTableFilters = (defaultFilters?: PaginatedFilter) => {
	const [, refrech] = useReducer((x) => x + 1, 0);
	const setFilters = (key: string, value: string) => {
		const url = new URL(window.location.href);

		if (value.trim() !== "") {
			url.searchParams.set(key, value);
		} else {
			url.searchParams.delete(key);
		}
		window.history.pushState({}, "", url.toString());
		refrech();
	};
	const filters = Object.fromEntries(
		new URL(window.location.href).searchParams.entries(),
	);
	return {
		filters:
			Object.keys(filters).length === 0
				? defaultFilters
				: (filters as PaginatedFilter),
		setFilters,
	};
};

export const SearchBar = () => {
	const { globalFilter, setGlobalFilter, setPage } =
		useContext(DataTableContext);
	const handleSetGlobalFilter = (filter: string) => {
		setPage!(0);
		setGlobalFilter!(filter);
	};

	return (
		<DebouncedInput
			value={globalFilter ?? ""}
			onDebouncedChange={(value) => handleSetGlobalFilter(value)}
			placeholder="Rechercher"
			w="20rem"
		/>
	);
};

export const Toolbar = () => {
	return (
		<Flex my="1rem" justifyContent="space-between">
			<SearchBar />
		</Flex>
	);
};

function DataTable({
	columns,
	paginatedQuery,
	w,
	flexGrow,
	defaultSortBy,
	defaultFilters,
	noDataComponent,
	hasRowsSelection = false,
	rowSelection,
	setRowSelection,
	handleCellClick,
	cellLink,
	rowSelectionLimit,
	canSelectAllRows,
	additionalParams,
	onDragEnd = undefined,
	toolbar = <Toolbar />,
}: DataTableProps) {
	// Column filters
	const { filters, setFilters } = useServerSideTableFilters(defaultFilters);
	const [selectAllRows, setSelectAllRows] = useState(false);
	// Global filters
	const [globalFilter, setGlobalFilter] = useState("");
	// Pagination
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 50,
	});
	const setPage = (page: number) => {
		setPagination({
			...pagination,
			pageIndex: page,
		});
	};
	// Sort
	const [sorting, setSorting] = useState<SortingState>(defaultSortBy ?? []);
	const handleSortingChange = (updatedOrValue: Updater<SortingState>) => {
		setSorting(updatedOrValue);
	};
	// Query
	const paginatedResponse = paginatedQuery(
		pagination.pageIndex,
		pagination.pageSize,
		globalFilter === "" ? undefined : globalFilter,
		filters,
		sorting.map((sort) => (sort.desc ? `-${sort.id}` : sort.id)),
		additionalParams,
	);
	const data = paginatedResponse ? paginatedResponse.data : [];
	const meta = paginatedResponse ? paginatedResponse.meta : undefined;
	// Columns
	if (hasRowsSelection) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const selectRow = (row: Row<any>) => {
			if (rowSelection && setRowSelection) {
				setSelectAllRows(false);
				const nextState = rowSelection[row.id] ? !rowSelection[row.id] : true;
				if (
					nextState === true &&
					rowSelectionLimit &&
					rowSelectionLimit < Object.keys(rowSelection).length
				) {
					return;
				}
				if (nextState === false) delete rowSelection[row.id];
				else rowSelection[row.id] = nextState;
				setRowSelection({ ...rowSelection });
			}
		};

		columns = [
			{
				id: "select-col",
				cell: ({ row }) => (
					<Flex alignItems="center" mr={4}>
						<Checkbox
							isChecked={rowSelection && rowSelection[row.id] !== undefined}
							onChange={() => selectRow(row)}
						/>
					</Flex>
				),
			},
			...columns,
		];
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSelectAllRows = (data: any) => {
		if (!canSelectAllRows || !setRowSelection) return;
		if (selectAllRows) {
			setRowSelection({});
			setSelectAllRows(false);
		} else {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const selectedRows = data.map((row: any) => row.id);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const newRowSelection = selectedRows.reduce((acc: any, row: any) => {
				acc[row] = true;
				return acc;
			}, {});
			setRowSelection(newRowSelection);
			setSelectAllRows(true);
		}
	};

	// Table
	const table = useReactTable({
		data: data,
		columns: columns,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting,
		},
		onSortingChange: handleSortingChange,
		manualPagination: true,
		manualFiltering: true,
		manualSorting: true,
		enableMultiRowSelection: true,
		getRowId: (row) => row.id,
	});

	return (
		<DataTableContext.Provider
			value={{
				filters,
				setFilters,
				globalFilter,
				setGlobalFilter,
				pagination,
				setPagination,
				setPage,
			}}
		>
			{toolbar}

			<VStack alignItems={"start"} bg="zinc.50" rounded="xl" px="4px" w="100%">
				<Flex w="100%" lineHeight={"44px"} px="1rem" py="4px">
					{table.getHeaderGroups().map((headerGroup) => {
						return headerGroup.headers.map((header, index) => {
							return (
								<Flex
									w={w ? w[hasRowsSelection ? index - 1 : index] : "auto"}
									flexGrow={
										flexGrow
											? flexGrow[hasRowsSelection ? index - 1 : index]
											: "auto"
									}
									key={header.id}
								>
									<Flex
										w={"100%"}
										onClick={header.column.getToggleSortingHandler()}
										justifyContent="space-between"
										pr="10px"
										color="gray.500"
									>
										{hasRowsSelection && canSelectAllRows && index === 0 && (
											<Checkbox
												isChecked={selectAllRows}
												onChange={() => handleSelectAllRows(data)}
											/>
										)}
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
										{{
											asc: <ChevronUpIcon h="44px"></ChevronUpIcon>,
											desc: <ChevronDownIcon h="44px"></ChevronDownIcon>,
										}[header.column.getIsSorted() as string] ?? null}
									</Flex>
								</Flex>
							);
						});
					})}
				</Flex>

				<RowContext onDragEnd={onDragEnd}>
					<Flex w="100%" direction={"column"} gap="2px" mb="4px">
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => {
								return (
									<Flex
										key={row.id}
										lineHeight={"44px"}
										alignItems={"center"}
										bg="white"
										px="1rem"
										cursor={handleCellClick ? "pointer" : "default"}
										_hover={{ backgroundColor: "zinc.50" }}
										_first={{ roundedTop: "xl" }}
										_last={{ roundedBottom: "xl" }}
									>
										{row.getVisibleCells().map((cell, index) => {
											return (
												<Box
													w={
														w ? w[hasRowsSelection ? index - 1 : index] : "auto"
													}
													flexGrow={
														flexGrow
															? flexGrow[hasRowsSelection ? index - 1 : index]
															: "auto"
													}
													key={cell.id}
													onClick={() => {
														handleCellClick &&
															handleCellClick({
																id: cell.id,
																row: row.original,
															});
													}}
												>
													<Link
														href={
															cellLink
																? cellLink({ id: cell.id, row: row.original })
																: undefined
														}
													>
														{flexRender(
															cell.column.columnDef.cell,
															cell.getContext(),
														)}
													</Link>
												</Box>
											);
										})}
									</Flex>
								);
							})
						) : noDataComponent ? (
							noDataComponent()
						) : (
							<Flex w="100%" justifyContent="center">
								<Box
									w="100%"
									rounded="xl"
									bg="white"
									textAlign="center"
									h="44px"
									lineHeight="44px"
									color="gray.500"
								>
									Pas de données pour le moment
								</Box>
							</Flex>
						)}
					</Flex>
				</RowContext>
			</VStack>

			<Flex w="100%" justifyContent="center" mt="1rem">
				<Box>
					<Button
						className="border rounded p-1"
						onClick={() => setPage(1)}
						isDisabled={
							meta === undefined || meta?.currentPage <= meta?.firstPage
						}
						variant="outline"
						mr="4px"
					>
						{"<<"}
					</Button>
					<Button
						className="border rounded p-1"
						onClick={() => meta && setPage(meta?.currentPage - 1)}
						isDisabled={
							meta === undefined || meta?.currentPage <= meta?.firstPage
						}
						variant="outline"
					>
						{"<"}
					</Button>
					<Box display="inline" mx="10px">
						Page&nbsp;
						{meta && meta.currentPage}&nbsp;/&nbsp;
						{meta && meta.lastPage}
					</Box>
					<Button
						className="border rounded p-1"
						onClick={() => meta && setPage(meta?.currentPage + 1)}
						isDisabled={
							meta === undefined || meta?.currentPage >= meta?.lastPage
						}
						variant="outline"
						mr="4px"
					>
						{">"}
					</Button>
					<Button
						className="border rounded p-1"
						onClick={() => meta && setPage(meta?.lastPage)}
						isDisabled={
							meta === undefined || meta?.currentPage >= meta?.lastPage
						}
						variant="outline"
					>
						{">>"}
					</Button>
				</Box>
			</Flex>
		</DataTableContext.Provider>
	);
}
export default DataTable;
